import type { ReactNode } from 'react';
import { DetailValue } from '@/components/common/DetailValue';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { Skeleton } from '@/components/ui/skeleton';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';

const AMOUNT_LABEL_CLASS = 'text-2xs font-medium tracking-wider text-muted-foreground uppercase';

export interface SummaryField<TRecord> {
  label: string;
  value: (record: TRecord) => ReactNode;
}

interface RecordSummaryPanelProps<TRecord> {
  ariaLabel: string;
  record: TRecord | undefined;
  badges: (record: TRecord) => ReactNode;
  title: (record: TRecord) => ReactNode;
  amountLabel: string;
  amount: (record: TRecord) => number;
  amountNote: (record: TRecord) => string | null;
  fields: readonly SummaryField<TRecord>[];
}

export function RecordSummaryPanel<TRecord>({
  ariaLabel,
  record,
  badges,
  title,
  amountLabel,
  amount,
  amountNote,
  fields,
}: RecordSummaryPanelProps<TRecord>) {
  const note = record === undefined ? null : amountNote(record);

  return (
    <section
      aria-label={ariaLabel}
      className="flex flex-col gap-4 rounded-2xl bg-card p-5 shadow-sm ring-1 ring-primary/10"
    >
      <div className="flex flex-col gap-2">
        {record === undefined ? (
          <>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-7 w-56 max-w-full" />
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">{badges(record)}</div>
            <h2 className="text-lg leading-snug font-semibold">{title(record)}</h2>
          </>
        )}
      </div>

      <div className="flex flex-col gap-1 border-y border-border py-4">
        <span className={AMOUNT_LABEL_CLASS}>{amountLabel}</span>
        {record === undefined ? (
          <>
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-4 w-24" />
          </>
        ) : (
          <>
            <span className="text-3xl leading-none font-bold tracking-tight tabular-nums">
              <span className="mr-1 text-base font-semibold text-muted-foreground">
                {TAKA_SYMBOL}
              </span>
              {formatNumber(amount(record))}
            </span>
            {note === null ? null : (
              <span className="text-xs text-muted-foreground tabular-nums">{note}</span>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {fields.map((field) => (
          <LabelWithValue key={field.label} label={field.label}>
            <DetailValue isLoading={record === undefined}>
              {record === undefined ? null : field.value(record)}
            </DetailValue>
          </LabelWithValue>
        ))}
      </div>
    </section>
  );
}
